import React, { useMemo } from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faShieldHalved } from "@fortawesome/pro-regular-svg-icons";
import { type UseMutateFunction } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { useConnections } from "src/api/connectApi/queries";
import type {
  CaseUpdateProviderResource,
  Connection,
} from "src/api/connectApi/types";
import type { FlowVersion } from "src/api/flowTypes";
import { Schema } from "src/api/flowTypes";
import { SchemaUpdate } from "src/api/flowVersionQueries";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import {
  type TemplateWithVersion,
  useCaseTemplateDataSchema,
  useEnsureCaseTemplateDataSchema,
} from "src/cases/queries/templates";
import { getTemplateIcon as getTemplateIconFromUtils } from "src/cases/templates/utils";
import {
  CaseTemplateAttributes,
  JsonApiResourceObjectCaseTemplateAttributesBaseModel,
  JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel,
} from "src/clients/case-management";
import { CaseUpdateNodeMetaTemplate } from "src/clients/flow-api";
import { isCaseUpdateNodeConnection } from "src/connections/model/inboundWebhook";
import { CaseUpdateNodeData } from "src/constants/NodeDataTypes";
import { NODE_TYPE } from "src/constants/NodeTypes";
import { toastActions } from "src/design-system/Toast/utils";
import {
  useAuthoringContext,
  useWorkspaceContext,
} from "src/router/routerContextHooks";
import { CaseFieldAccessInfo } from "src/schema/schemaEditor/CaseFieldAccessInfo";
import { CaseSchemaHint } from "src/schema/schemaEditor/CaseSchemaHint";
import { CaseTemplateSelector } from "src/schema/schemaEditor/CaseTemplateSelector";
import {
  createSelectedSchema,
  SelectedTemplateInfo,
  TemplateMetadata,
  transformTemplateToFlowSchema,
} from "src/schema/schemaEditor/caseSchemaUtils";
import { SchemaEditorProps } from "src/schema/schemaEditor/types";
import { useCaseSchemaEditorState } from "src/schema/schemaEditor/useCaseSchemaEditorState";
import {
  getTemplateMetadata,
  useCaseTemplateInputSchemaError,
} from "src/schema/schemaEditor/useCaseTemplateInputSchemaValidation";
import {
  ARCHIVED_TOAST_ID,
  CHANGED_TOAST_ID,
} from "src/schema/schemaEditor/useErrorToasts";
import { useUpdateSchemaWithFlowTags } from "src/schema/schemaEditor/useUpdateSchemaWithFlowTags";
import { GraphStore } from "src/store/GraphStore";
import { useGraphStore } from "src/store/StoreProvider";
import { logger } from "src/utils/logger";

export type CaseSchemaEditorProps = SchemaEditorProps & {
  mutateSchema: UseMutateFunction<
    FlowVersion,
    AxiosError,
    SchemaUpdate,
    unknown
  >;
};

export type CaseSchemaWithMetadata = Schema & {
  _template_metadata?: TemplateMetadata;
};

type DropdownTemplate = JsonApiResourceObjectCaseTemplateAttributesBaseModel & {
  versions?: JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel[];
};

type DropdownSelection = {
  templateId: string;
  versionId: string;
};

const CASE_DATA_PATH = "data.case";

const getTemplateIcon = (icon: string | null | undefined): IconProp => {
  return getTemplateIconFromUtils(icon) ?? faShieldHalved;
};

const transformTemplatesToDropdownFormat = (
  templatesData: TemplateWithVersion[] | undefined,
): DropdownTemplate[] => {
  if (!templatesData) return [];

  const templateMap: Record<string, DropdownTemplate> = {};

  templatesData.forEach((template) => {
    if (!templateMap[template.templateId]) {
      templateMap[template.templateId] = {
        id: template.templateId,
        type: "case_template",
        attributes: template.template.attributes,
        versions: [],
      };
    }

    const dropdownTemplate = templateMap[template.templateId];
    if (dropdownTemplate?.versions) {
      dropdownTemplate.versions.push(template.version);
    }
  });

  return Object.values(templateMap);
};

const extractCurrentTemplate = (
  currentSchema: Schema | undefined,
  templatesData: TemplateWithVersion[] | undefined,
): SelectedTemplateInfo | null => {
  const caseSchema = currentSchema?.properties?.case as
    | CaseSchemaWithMetadata
    | undefined;

  if (!currentSchema?.properties?.case || !caseSchema) {
    return null;
  }

  const metadata = caseSchema._template_metadata;

  if (metadata?.templateId && metadata?.versionId) {
    const template = templatesData?.find(
      (t) =>
        t.templateId === metadata.templateId &&
        t.versionId === metadata.versionId,
    );

    if (template) {
      return {
        templateId: template.templateId,
        versionId: template.versionId,
        templateName: template.templateName,
        versionName: template.versionName,
        icon: getTemplateIcon(template.icon),
      };
    }

    // If template not found in templatesData but we have metadata (possibly archived),
    // use the saved names from metadata
    if (metadata.templateName && metadata.versionName) {
      return {
        templateId: metadata.templateId,
        versionId: metadata.versionId,
        templateName: metadata.templateName,
        versionName: metadata.versionName,
        icon: getTemplateIcon(null),
      };
    }
  }

  return {
    templateId: "unknown",
    versionId: "unknown",
    templateName: "Case Template",
    versionName: "Unknown",
    icon: getTemplateIcon(null),
  };
};

export const CaseSchemaEditor: React.FC<CaseSchemaEditorProps> = ({
  isReadonly,
  formMethods: _formMethods,
  mutateSchema,
}) => {
  const { workspace } = useWorkspaceContext();
  const { version } = useAuthoringContext();
  const graphStore = useGraphStore();
  const currentSchema = version?.input_schema;
  const { templateId, versionId } = getTemplateMetadata(currentSchema);
  const error = useCaseTemplateInputSchemaError(currentSchema);

  const updateSchemaWithFlowTags = useUpdateSchemaWithFlowTags({
    mutateSchema,
  });

  const { data: templateSchemaResponse, isLoading: isLoadingSchema } =
    useCaseTemplateDataSchema(templateId, versionId, {
      throwOnError: false,
    });

  const { promise: connectionsPromise } = useConnections(workspace.base_url, {
    showInternalResources: true,
  });

  const { templatesData, isLoadingTemplates } = useCaseSchemaEditorState();

  const selectedTemplate = useMemo(
    () => extractCurrentTemplate(currentSchema, templatesData),
    [currentSchema, templatesData],
  );

  const selectedSchema = useMemo(
    () =>
      createSelectedSchema(
        templateSchemaResponse,
        templatesData,
        selectedTemplate,
        currentSchema,
      ),
    [templateSchemaResponse, templatesData, selectedTemplate, currentSchema],
  );

  const dropdownTemplates = useMemo(
    () => transformTemplatesToDropdownFormat(templatesData),
    [templatesData],
  );

  const handleTemplateClear = async () => {
    try {
      await updateSchemaWithFlowTags({
        type: "input",
        schema: {
          $schema: "https://json-schema.org/draft/2020-12/schema",
          type: "object",
          properties: {},
          required: [],
          sensitive: [],
          order: [],
        },
      });
    } catch (error) {
      logger.error("Error clearing template schema:", error);

      toastActions.failure({
        title: "Unable to clear template schema",
        description: "Please refresh the page and try again.",
      });
    }
  };

  const ensureCaseTemplateDataSchema = useEnsureCaseTemplateDataSchema();

  const handleTemplateSelection = async (selection: DropdownSelection) => {
    const { templateId, versionId } = selection;
    const selectedTemplateData = templatesData?.find(
      (t) => t.templateId === templateId && t.versionId === versionId,
    );

    if (!selectedTemplateData) return;

    // Dismiss toasts if they're still visible
    toastActions.dismiss(ARCHIVED_TOAST_ID);
    toastActions.dismiss(CHANGED_TOAST_ID);

    const templateSchemaResponse = await ensureCaseTemplateDataSchema(
      templateId,
      versionId,
    );

    const caseJsonSchema =
      templateSchemaResponse.data.attributes.case_json_schema;

    if (selectedTemplateData) {
      const wrappedSchema = transformTemplateToFlowSchema(caseJsonSchema, {
        templateId: selectedTemplateData.templateId,
        versionId: selectedTemplateData.versionId,
        etag: selectedTemplateData.version.attributes.etag,
        templateName: selectedTemplateData.templateName,
        versionName: selectedTemplateData.versionName,
      });

      try {
        await updateSchemaWithFlowTags({
          type: "input",
          schema: wrappedSchema,
        });
      } catch (error) {
        logger.error("Error updating flow tags:", error);

        toastActions.failure({
          title: "Unable to select Case template",
          description: "Please refresh the page and try again.",
        });
      }
    } else {
      logger.error("Selected template data not found:", {
        templateId,
        versionId,
      });
    }

    if (isReadonly) return;

    try {
      const connectionsData = await connectionsPromise;

      const caseUpdateConnection = connectionsData.find(
        isCaseUpdateNodeConnection,
      );

      const hasUpdateCaseNode = graphStore.nodesArray.some(
        (node) =>
          node.type === NODE_TYPE.CASE_UPDATE_NODE &&
          (node.data as CaseUpdateNodeData)?.connection_id ===
            caseUpdateConnection?.id,
      );

      if (hasUpdateCaseNode) return;

      const defaultTemplateSelection: CaseUpdateNodeMetaTemplate = {
        id: selectedTemplateData.templateId,
        version_id: selectedTemplateData.versionId,
        etag: parseInt(selectedTemplateData.version.attributes.etag ?? "0", 10),
      };

      await triggerCaseUpdateNodeCreation({
        selectedTemplate: selectedTemplateData.template.attributes,
        defaultTemplateSelection,
        connections: connectionsData,
        graphStore,
        setClickedVisibleEdgeId: graphStore.setClickedVisibleEdgeId,
        getClickedVisibleEdgeId: (): string | null =>
          graphStore.clickedVisibleEdgeId ?? null,
      });

      toastActions.success({
        title: "Update Case node added",
        description: `An Update Case node has been added to your flow for the ${selectedTemplateData.template.attributes.template_name} template.`,
      });
    } catch {
      toastActions.failure({
        title: "Unable to add Update Case node",
        description:
          "The case template was configured successfully, but we couldn't automatically add the Update Case node. You can add it manually from the node palette.",
      });
    }
  };

  return (
    <div data-loc="case-schema-editor-container">
      <CaseTemplateSelector
        dropdownTemplates={dropdownTemplates}
        error={error}
        formMethods={_formMethods}
        isLoadingSchema={isLoadingSchema}
        isLoadingTemplates={isLoadingTemplates}
        isReadonly={isReadonly}
        mutateSchema={mutateSchema}
        selectedTemplate={selectedTemplate}
        onTemplateClear={handleTemplateClear}
        onTemplateSelection={handleTemplateSelection}
      />
      {selectedTemplate && <CaseFieldAccessInfo />}
      {isLoadingSchema && selectedTemplate && (
        <div className="mt-4">
          <SkeletonPlaceholder height="h-32" />
        </div>
      )}
      {selectedSchema && !isLoadingSchema && (
        <CaseSchemaHint
          rootPath={CASE_DATA_PATH}
          selectedSchema={selectedSchema}
        />
      )}
    </div>
  );
};

const getOutputEdge = (graphStore: GraphStore): string => {
  const outputNode = graphStore.nodesArray.find(
    (n) => n.type === NODE_TYPE.OUTPUT_NODE,
  );
  if (!outputNode) {
    throw new Error("No output node found in the graph store.");
  }

  const incomingEdge = graphStore.edgesArray.find(
    (e) => e.target === outputNode.id,
  );
  if (!incomingEdge) {
    throw new Error(`No incoming edge found for output node: ${outputNode.id}`);
  }
  return incomingEdge.id;
};

export const triggerCaseUpdateNodeCreation = async (params: {
  selectedTemplate: CaseTemplateAttributes;
  defaultTemplateSelection?: CaseUpdateNodeMetaTemplate;
  connections: Connection[];
  graphStore: GraphStore;
  setClickedVisibleEdgeId: (id: string | null) => void;
  getClickedVisibleEdgeId: () => string | null;
}) => {
  const {
    defaultTemplateSelection,
    connections,
    graphStore,
    setClickedVisibleEdgeId,
    getClickedVisibleEdgeId,
  } = params;

  // 1. Find the case update webhook connection
  const connection = connections.find(isCaseUpdateNodeConnection);
  if (!connection) {
    throw new Error("Case update webhook connection not found");
  }

  // 2. Get the resource config for 'case_update_event'
  const resourceConfig = connection.resource_configs[0];
  if (!resourceConfig) {
    throw new Error(
      `No resource config found for case update connection: ${connection.id}`,
    );
  }

  // 3. Find the output node and its incoming edge
  const incomingEdgeId = getOutputEdge(graphStore);

  // 4. Capture current state to ensure it's fresh
  const savedClickedEdgeId = getClickedVisibleEdgeId();

  // 5. Temporarily set clickedVisibleEdgeId, add node at output's incoming edge, then restore
  setClickedVisibleEdgeId(incomingEdgeId);

  // 6. Add Update Case Node
  try {
    const nodeId = await graphStore.addNode({
      nodeType: NODE_TYPE.CASE_UPDATE_NODE,
      providerResource: {
        provider: connection.provider,
        resource: resourceConfig.resource,
      } as CaseUpdateProviderResource,
      connectionId: connection.id,
      resourceConfigId: resourceConfig.id,
    });

    if (!nodeId) {
      throw new Error("Failed to create Update Case node.");
    }

    if (defaultTemplateSelection) {
      graphStore.updateNode<CaseUpdateNodeData>({
        nodeId,
        newData: {
          template: defaultTemplateSelection,
        },
      });
    }
  } catch (error) {
    logger.error("Failed to add Update Case Node:", error);
    throw new Error("Failed to add Update Case Node: " + error);
  } finally {
    // Always restore the saved state, even if addNode fails
    setClickedVisibleEdgeId(savedClickedEdgeId);
  }
};
